import { useState, useEffect } from "react";
import { AuthProvider, useAuth } from "./hooks/useAuth";
import SessionWarning from "./components/SessionWarning";
import BedBoard from "./components/BedBoard";
import ChartAudit from "./components/ChartAudit";
import OutreachTracker from "./components/OutreachTracker";
import RevenueTracker from "./components/RevenueTracker";
import ChangePassword from "./components/ChangePassword";

const F = "'DM Sans', system-ui, sans-serif";

const TOOLS = [
  { key: "bed_board", name: "Bed Board", desc: "Track bed status, occupancy, and house notes.", icon: "🏥", color: "#0369a1", bg: "#e0f2fe" },
  { key: "chart_audit", name: "Chart Audit", desc: "Treatment plan compliance and session tracking.", icon: "📋", color: "#7c3aed", bg: "#ede9fe" },
  { key: "outreach_tracker", name: "Outreach Tracker", desc: "Log outreach visits and track contacts.", icon: "🤝", color: "#059669", bg: "#d1fae5" },
  { key: "revenue_tracker", name: "Revenue Tracker", desc: "Track payments and project revenue.", icon: "💰", color: "#0d9488", bg: "#ccfbf1" },
];

const ROLE_ACCESS = {
  admin: { bed_board: true, chart_audit: true, outreach_tracker: true, revenue_tracker: true },
  director: { bed_board: true, chart_audit: true, outreach_tracker: true, revenue_tracker: true },
  supervisor: { bed_board: true, chart_audit: true, outreach_tracker: true, revenue_tracker: false },
  staff: { bed_board: true, chart_audit: false, outreach_tracker: true, revenue_tracker: false },
};

const ROLE_LABELS = { admin: "Administrator", director: "Director", supervisor: "Supervisor", staff: "Staff" };

// ===== LOGIN SCREEN =====
function LoginScreen() {
  const { login, loading, error } = useAuth();
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");

  const handleSignIn = async () => {
    if (!email || !pw) return;
    try { await login(email, pw); } catch {}
  };

  const inp = { width: "100%", padding: "0.625rem 0.75rem", border: "1.5px solid #d1d5db", borderRadius: "8px", fontSize: "0.9375rem", fontFamily: F, outline: "none", boxSizing: "border-box" };

  return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "linear-gradient(145deg, #0f172a, #1e3a5f, #134e4a)", padding: "1.5rem" }}>
      <div style={{ width: "100%", maxWidth: "420px" }}>
        <div style={{ textAlign: "center", marginBottom: "2rem" }}>
          <div style={{ fontSize: "2.5rem" }}>🏥</div>
          <h1 style={{ fontSize: "1.625rem", fontWeight: "700", color: "#f0fdfa", fontFamily: F }}>ITG Academy</h1>
          <p style={{ fontSize: "0.875rem", color: "#94a3b8", fontFamily: F }}>Staff Operations Portal</p>
        </div>
        <div style={{ background: "#fff", borderRadius: "12px", padding: "2rem", boxShadow: "0 25px 50px -12px rgba(0,0,0,0.4)" }}>
          <h2 style={{ fontSize: "1.125rem", fontWeight: "600", color: "#0f172a", marginBottom: "1.5rem", fontFamily: F }}>Sign in</h2>
          {error && <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: "8px", padding: "0.75rem", marginBottom: "1rem", fontSize: "0.875rem", color: "#991b1b" }}>{error}</div>}
          <div style={{ marginBottom: "1rem" }}>
            <label style={{ display: "block", fontSize: "0.8125rem", fontWeight: "500", color: "#374151", marginBottom: "0.25rem", fontFamily: F }}>Email</label>
            <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@itgacademy.com" style={inp}
              onKeyDown={e => e.key === "Enter" && handleSignIn()} />
          </div>
          <div style={{ marginBottom: "1.5rem" }}>
            <label style={{ display: "block", fontSize: "0.8125rem", fontWeight: "500", color: "#374151", marginBottom: "0.25rem", fontFamily: F }}>Password</label>
            <input type="password" value={pw} onChange={e => setPw(e.target.value)} placeholder="Password" style={inp}
              onKeyDown={e => e.key === "Enter" && handleSignIn()} />
          </div>
          <button onClick={handleSignIn} disabled={loading}
            style={{ width: "100%", padding: "0.75rem", background: loading ? "#94a3b8" : "linear-gradient(135deg, #0f172a, #134e4a)", color: "#fff", border: "none", borderRadius: "8px", fontSize: "0.9375rem", fontWeight: "600", cursor: loading ? "not-allowed" : "pointer", fontFamily: F }}>
            {loading ? "Signing in..." : "Sign In"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ===== HEADER =====
function Header({ activeTool, onBack, onChangePw }) {
  const { user, logout } = useAuth();
  return (
    <header style={{ background: "#0f172a", color: "#fff", padding: "0 1.25rem", height: "50px", display: "flex", alignItems: "center", justifyContent: "space-between", position: "sticky", top: 0, zIndex: 100, boxShadow: "0 1px 3px rgba(0,0,0,0.3)" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "0.625rem" }}>
        {activeTool && <button onClick={onBack} style={{ background: "rgba(255,255,255,0.1)", border: "none", color: "#94a3b8", padding: "0.25rem 0.5rem", borderRadius: "4px", fontSize: "0.6875rem", cursor: "pointer", fontFamily: F }}>← Dashboard</button>}
        <span style={{ fontSize: "1rem" }}>🏥</span>
        <span style={{ fontWeight: "700", fontSize: "0.875rem", fontFamily: F }}>ITG Academy</span>
        {activeTool && <span style={{ color: "#64748b", fontSize: "0.75rem", fontFamily: F }}>/ {activeTool.name}</span>}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: "0.6875rem", fontWeight: "600", color: "#e2e8f0", fontFamily: F }}>{user.name}</div>
          <div style={{ fontSize: "0.5625rem", color: "#64748b", fontFamily: F }}>{ROLE_LABELS[user.role]}</div>
        </div>
        <div style={{ width: "28px", height: "28px", borderRadius: "6px", background: "linear-gradient(135deg, #0d9488, #0369a1)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "0.6875rem", fontWeight: "700", fontFamily: F, color: "#fff" }}>
          {user.name.split(" ").map(n => n[0]).join("")}
        </div>
        <button onClick={onChangePw} style={{ background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.15)", color: "#94a3b8", padding: "0.25rem 0.5rem", borderRadius: "4px", fontSize: "0.625rem", cursor: "pointer", fontFamily: F }}>Change Password</button>
        <button onClick={logout} style={{ background: "rgba(239,68,68,0.15)", border: "1px solid rgba(239,68,68,0.3)", color: "#fca5a5", padding: "0.25rem 0.5rem", borderRadius: "4px", fontSize: "0.625rem", cursor: "pointer", fontFamily: F }}>Sign Out</button>
      </div>
    </header>
  );
}

// ===== DASHBOARD =====
function Dashboard({ onOpenTool }) {
  const { user } = useAuth();
  const access = user.tool_access ? user.tool_access.split(",").reduce((a, k) => { a[k.trim()] = true; return a; }, {}) : (ROLE_ACCESS[user.role] || {});
  const tools = TOOLS.filter(t => access[t.key]);
  const now = new Date();
  const greeting = now.getHours() < 12 ? "Good morning" : now.getHours() < 17 ? "Good afternoon" : "Good evening";

  return (
    <div style={{ minHeight: "calc(100vh - 50px)", background: "#f8f9fb" }}>
      <div style={{ maxWidth: "1100px", margin: "0 auto", padding: "1.5rem" }}>
        <div style={{ marginBottom: "1.25rem" }}>
          <h1 style={{ fontSize: "1.375rem", fontWeight: "700", color: "#0f172a", fontFamily: F }}>{greeting}, {user.name.split(" ")[0]}</h1>
          <p style={{ fontSize: "0.8125rem", color: "#64748b", fontFamily: F }}>{now.toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" })}</p>
        </div>
        <div style={{ fontSize: "0.6875rem", fontWeight: "600", color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: "0.625rem", fontFamily: F }}>Your Tools</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: "0.875rem" }}>
          {tools.map(t => (
            <div key={t.key} onClick={() => onOpenTool(t)} style={{ background: "#fff", borderRadius: "10px", border: "1px solid #e2e8f0", padding: "1.125rem", cursor: "pointer", borderTop: "3px solid " + t.color }}>
              <div style={{ display: "flex", gap: "0.625rem" }}>
                <div style={{ width: "40px", height: "40px", borderRadius: "8px", background: t.bg, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.25rem" }}>{t.icon}</div>
                <div>
                  <h3 style={{ fontSize: "0.9375rem", fontWeight: "700", color: "#0f172a", fontFamily: F }}>{t.name}</h3>
                  <p style={{ fontSize: "0.75rem", color: "#64748b", fontFamily: F, lineHeight: "1.4", marginTop: "2px" }}>{t.desc}</p>
                </div>
              </div>
              <div style={{ marginTop: "0.625rem", paddingTop: "0.5rem", borderTop: "1px solid #f1f5f9" }}>
                <span style={{ fontSize: "0.6875rem", color: t.color, fontWeight: "600", fontFamily: F }}>Open Tool →</span>
              </div>
            </div>
          ))}
        </div>
        {tools.length < TOOLS.length && (
          <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: "8px", padding: "0.625rem 0.875rem", marginTop: "0.875rem", display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <span>🔒</span>
            <p style={{ fontSize: "0.6875rem", color: "#94a3b8", fontFamily: F }}>{TOOLS.length - tools.length} more tool{TOOLS.length - tools.length > 1 ? "s" : ""} with elevated access.</p>
          </div>
        )}
      </div>
    </div>
  );
}

// ===== PLACEHOLDER (for tools not yet wired) =====
function Placeholder({ tool }) {
  return (
    <div style={{ minHeight: "calc(100vh - 50px)", background: "#f8f9fb", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ textAlign: "center", padding: "3rem" }}>
        <div style={{ fontSize: "3rem", opacity: 0.3 }}>🔧</div>
        <h2 style={{ fontSize: "1.25rem", fontWeight: "600", color: "#334155", fontFamily: F }}>{tool.name} — Connecting to Database</h2>
        <p style={{ fontSize: "0.8125rem", color: "#94a3b8", fontFamily: F, marginTop: "0.5rem" }}>This tool will be wired to the API shortly.</p>
      </div>
    </div>
  );
}

// ===== APP SHELL =====
function AppShell() {
  const { isAuthenticated } = useAuth();
  const [tool, setTool] = useState(null);
  const [showPw, setShowPw] = useState(false);

  if (!isAuthenticated) return <LoginScreen />;

  const renderTool = () => {
    if (!tool) return <Dashboard onOpenTool={setTool} />;
    if (tool.key === "bed_board") return <BedBoard />;
    if (tool.key === "chart_audit") return <ChartAudit />;
    if (tool.key === "outreach_tracker") return <OutreachTracker />;
    if (tool.key === "revenue_tracker") return <RevenueTracker />;
    return <Placeholder tool={tool} />;
  };

  return (
    <div style={{ minHeight: "100vh", background: "#f8f9fb" }}>
      <Header activeTool={tool} onBack={() => setTool(null)} onChangePw={() => setShowPw(true)} />
      <SessionWarning />
      {showPw && <ChangePassword onClose={() => setShowPw(false)} />}
      {renderTool()}
    </div>
  );
}

// ===== ROOT =====
export default function App() {
  useEffect(() => {
    const link = document.createElement("link");
    link.href = "https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap";
    link.rel = "stylesheet";
    document.head.appendChild(link);
    return () => document.head.removeChild(link);
  }, []);

  return (
    <AuthProvider>
      <AppShell />
    </AuthProvider>
  );
}
